// The client page's single payload — GET /api/clients/:id/brief.
//
// One call, one arrangement. Every string in this payload is a column value, a number counted
// from columns, or a template with slots filled by the first two (see server/utils/briefQueries.ts):
// the page ARRANGES, it never derives. The types below mirror BriefPayload exactly — when the
// server shape changes, this file changes with it and `bunx tsc --noEmit` says so.
import { request } from './client.js';
import type { RailCell } from '../../shared/rail.js';

/** Routes are CONSTANT per field/rule — they never vary with the value; only anchors carry refs. */
export type BriefRoute =
  | { kind: 'in-page'; anchor: string }
  | { kind: 'screen'; screen: string; engagement_id?: number; anchor?: string | null }
  | { kind: 'related'; related: Record<string, unknown> }
  | null;

export type FieldKey = 'engagements' | 'day' | 'ball' | 'last_contact' | 'new';
export type FieldTone = 'ink' | 'warn' | 'ok' | 'err' | 'nil';

/**
 * One rail cell. The five arrive in a fixed order and none is ever omitted.
 *
 * This is `RailCell` from src/shared/rail.ts — the SAME declaration the server narrows into
 * `briefQueries.Field` — with this page's key and tone unions applied and its route union
 * spelled out. It is no longer a hand-mirror that can drift (FEASIBILITY BL-5).
 */
export interface BriefField extends RailCell {
  key: FieldKey;
  value: string;
  unit: string | null;
  detail: string;
  tone: FieldTone;
  route: BriefRoute;
}

export interface BriefQueueItem {
  rule: string;
  weight: number;
  title: string;
  why: string;
  chips: string[];
  route: BriefRoute;
  engagement: { id: number; code: string } | null;
  /** Rule 40 only — a co-existence check, labelled 'derived check' in the UI. */
  derived?: boolean;
}

export interface BriefRouteCell {
  key: 'documents' | 'register' | 'agent' | 'proposals' | 'contracting' | 'acceptance';
  count: number;
  qualifier: string;
  screen: string;
}

export interface BriefGate {
  gate: string;
  title: string;
  all_met: boolean;
  auto_met: number;
  auto_total: number;
  manual_met: number;
  manual_total: number;
  /** One pip per automatic condition — labels and details are the ENGINE's own strings. */
  pips: { label: string; met: boolean; detail: string }[];
}

export interface BriefEngagement {
  id: number;
  code: string;
  name: string;
  current_phase: string;
  phase_label: string;
  phase_index: number;
  phase_count: number;
  currency: string;
  estimate_value: string | null;
  kickoff_date: string | null;
  archived_at: string | null;
  created_at: string;
  gate: BriefGate | null;
  gate_error: string | null;
  gate_skipped: string | null;
  routes: BriefRouteCell[];
}

export interface BriefLatestEntry {
  layer: 'document' | 'event';
  date: string | null;
  ref: string;
  kind: string | null;
  title: string;
  summary: string | null;
  superseded_by_ref: string | null;
  new: boolean;
}

export interface BriefClient {
  id: number;
  name: string;
  status: string;
  legal_name: string | null;
  short_name: string | null;
  sector: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  postcode: string | null;
  country: string | null;
  address: string | null;
  notes: string | null;
  created_at: string;
}

export interface BriefContact {
  id: number;
  client_id: number;
  name: string;
  role_title: string | null;
  email: string | null;
  phone: string | null;
  is_sponsor: boolean;
  /** Engagement codes this contact is the named sponsor of — the answer G0 checks. */
  sponsor_of: string[];
}

/** A person named on an event who matches no contact — evidence, not a record. */
export interface BriefGhost {
  name: string;
  email: string | null;
  events: number;
  first_seen: string;
  last_seen: string;
}

export interface BriefFact {
  id: number;
  key: string;
  label: string;
  value: string;
  as_of: string | null;
  stated_by: string | null;
  quote: string | null;
  uncatalogued: boolean;
  /** Agent-created and never confirmed by a human — the ◇ mark. */
  unconfirmed: boolean;
  source_document_ref: string;
  history_count: number;
}

export interface BriefFactGroup {
  group: string;
  prefixes: string[];
  count: number;
  facts: BriefFact[];
}

export interface BriefPayload {
  client: BriefClient;
  engagements: BriefEngagement[];
  readout: BriefField[];
  queue: BriefQueueItem[];
  momentum: {
    weeks: { week_start: string; docs: number; events: number }[];
    suppressed: boolean;
    note: string | null;
    touches: number;
    weeks_count: number;
    days_since_last_contact: number | null;
    longest_silence_days: number | null;
    events_on_record: number;
  };
  latest: BriefLatestEntry[];
  /** The date NEW counts from — always rendered, never inferred client-side. */
  latest_since: string;
  people: { contacts: BriefContact[]; ghosts: BriefGhost[] };
  facts: { groups: BriefFactGroup[]; note: string | null };
  /** Non-empty means render degraded per-card — never an error page. */
  problems: { source: string; engagement_code: string | null; message: string }[];
  generated_at: string;
}

/** Every date on this page reads the same way the payload's own detail strings do. */
export const fmtBriefDate = (iso: string | null | undefined): string =>
  iso ? `${String(iso).slice(8, 10)}.${String(iso).slice(5, 7)}.${String(iso).slice(0, 4)}` : '—';

/** The page hands every route to one handler — the zones never touch the hash themselves. */
export type RouteHandler = (route: BriefRoute) => void;

export const briefApi = {
  /** One call. Upserts the operator's visit server-side, so NEW counts from the PREVIOUS one. */
  get: (clientId: number) => request<BriefPayload>(`/clients/${clientId}/brief`),
};

export default briefApi;
